import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
const _excluded = ["children"],
  _excluded2 = ["children"],
  _excluded3 = ["children"],
  _excluded4 = ["children"],
  _excluded5 = ["children"],
  _excluded6 = ["children"];
import { Box, Button, Heading } from '@wix/design-system';
import { useIsMobile } from '@wix/core-domain-search';
import { DomainSearchSuiteModule, SearchRecommendationLayout } from '@wix/domains-search-suite';
import React, { useContext, useEffect, useRef } from 'react';
import { usePreSuggestions } from '../../hooks/usePreSuggestions';
import { PRE_SUGGESTIONS_FALLBACK_QUERY } from '../../hooks/usePreSuggestionsQuery';
import { LazyRecommendationLabel } from '../LazyRecommedationLabel/LazyRecommendationLabel';
import styles from './preSuggestionsOptimized.scss';
const CardActionContext = /*#__PURE__*/React.createContext(undefined);
const CardDomainContext = /*#__PURE__*/React.createContext(undefined);
const PreSuggestionsOptimizedLayout = {
  SearchRecommendationListContainer: _ref => {
    let children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);
    const isMobile = useIsMobile();
    return /*#__PURE__*/React.createElement(Box, _extends({
      direction: isMobile ? 'vertical' : 'horizontal',
      display: "flex",
      width: "100%",
      gap: "16px"
    }, !isMobile && {
      flexWrap: 'wrap'
    }, {
      paddingBottom: "12px"
    }, props), children);
  },
  SearchRecommendationContainer: _ref2 => {
    let children = _ref2.children,
      className = _ref2.className,
      dataHook = _ref2.dataHook;
    const domainRef = useRef();
    const cardAction = useContext(CardActionContext);
    const handleCardClick = () => {
      const recommendation = cardAction == null ? void 0 : cardAction.recommendations.find(_ref3 => {
        let domain = _ref3.domain;
        return domain === domainRef.current;
      });
      if (!cardAction || cardAction.disabled || !recommendation) {
        return;
      }
      void cardAction.onGetItClick(recommendation);
    };
    return /*#__PURE__*/React.createElement(CardDomainContext.Provider, {
      value: domainRef
    }, /*#__PURE__*/React.createElement("div", {
      className: [styles.card, className].filter(Boolean).join(' '),
      "data-hook": dataHook,
      onClick: handleCardClick
    }, children));
  },
  SearchRecommendationSkeletonContainer: _ref4 => {
    let children = _ref4.children;
    return /*#__PURE__*/React.createElement("div", {
      className: styles.card
    }, children);
  },
  DomainInfoContainer: _ref5 => {
    let children = _ref5.children,
      props = _objectWithoutPropertiesLoose(_ref5, _excluded2);
    return /*#__PURE__*/React.createElement(Box, _extends({
      direction: "vertical",
      gap: "12px"
    }, props), children);
  },
  Skeleton: () => /*#__PURE__*/React.createElement(SearchRecommendationLayout.Skeleton, {
    limit: 3
  }),
  Label: props => /*#__PURE__*/React.createElement(SearchRecommendationLayout.Label, _extends({}, props, {
    primary: true,
    className: styles.premiumBadge
  })),
  GetItButtonContainer: _ref6 => {
    let children = _ref6.children;
    return /*#__PURE__*/React.createElement("div", {
      className: styles.getItButton,
      onClick: event => event.stopPropagation()
    }, children);
  },
  GetItButton: _ref7 => {
    let children = _ref7.children,
      props = _objectWithoutPropertiesLoose(_ref7, _excluded3);
    return /*#__PURE__*/React.createElement(Button, _extends({
      size: "medium",
      skin: "dark"
    }, props, {
      priority: "primary"
    }), children);
  },
  TitleDisplay: props => {
    const domainRef = useContext(CardDomainContext);
    useEffect(() => {
      if (domainRef) {
        domainRef.current = props.title;
      }
    }, [domainRef, props.title]);
    return /*#__PURE__*/React.createElement(Heading, {
      size: "large",
      className: styles.domainTitle
    }, props.title);
  },
  Divider: () => /*#__PURE__*/React.createElement(React.Fragment, null),
  HoverBox: _ref8 => {
    let children = _ref8.children,
      props = _objectWithoutPropertiesLoose(_ref8, _excluded4);
    return /*#__PURE__*/React.createElement(Box, _extends({}, props, {
      flex: 1,
      className: ""
    }), children);
  },
  ActionsContainer: _ref9 => {
    let children = _ref9.children,
      props = _objectWithoutPropertiesLoose(_ref9, _excluded5);
    return /*#__PURE__*/React.createElement(Box, _extends({
      gap: "24px",
      verticalAlign: "middle",
      align: "space-between"
    }, props), children);
  },
  PricingWrapper: _ref0 => {
    let children = _ref0.children,
      props = _objectWithoutPropertiesLoose(_ref0, _excluded6);
    return /*#__PURE__*/React.createElement(Box, _extends({}, props, {
      gap: "4px",
      align: "left",
      className: styles.pricing
    }), children);
  }
};
export const PreSuggestionsOptimized = function (_temp) {
  let _ref1 = _temp === void 0 ? {} : _temp,
    _ref1$fallbackQuery = _ref1.fallbackQuery,
    fallbackQuery = _ref1$fallbackQuery === void 0 ? PRE_SUGGESTIONS_FALLBACK_QUERY : _ref1$fallbackQuery;
  const _usePreSuggestions = usePreSuggestions(fallbackQuery),
    recommendations = _usePreSuggestions.recommendations,
    isLoading = _usePreSuggestions.isLoading,
    isPricingLoading = _usePreSuggestions.isPricingLoading,
    matchLabels = _usePreSuggestions.matchLabels,
    isLoadingLabels = _usePreSuggestions.isLoadingLabels,
    disabled = _usePreSuggestions.disabled,
    hidden = _usePreSuggestions.hidden,
    onGetItClick = _usePreSuggestions.onGetItClick;
  if (hidden) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    align: "center"
  }, /*#__PURE__*/React.createElement(CardActionContext.Provider, {
    value: {
      recommendations,
      disabled,
      onGetItClick
    }
  }, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.SearchRecommendationList, {
    items: recommendations,
    reportImpressions: true,
    isLoading: isLoading,
    isPricingLoading: isPricingLoading,
    Layouts: {
      ...PreSuggestionsOptimizedLayout,
      LabelContainer: LazyRecommendationLabel({
        isLoading: isLoadingLabels,
        matchLabels,
        primary: true,
        labelClassName: styles.premiumBadge
      })
    },
    disabled: disabled,
    onClickGetIt: onGetItClick,
    firstSuggestionPrimary: false
  })));
};
//# sourceMappingURL=PreSuggestionsOptimized.js.map